import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { backendConfig } from '../config/backendConfig';
import { authFetch } from '../utils/authFetch';

// Cache for access check results (keyed by user UID)
const accessCheckCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper to get cached result from sessionStorage
const getCachedResult = (userId) => {
  try {
    const cacheKey = `mentor_access_${userId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < CACHE_DURATION) {
        return parsed;
      } else {
        sessionStorage.removeItem(cacheKey);
      }
    }
  } catch (e) {
    // Ignore sessionStorage errors
  }
  return null;
};

// Helper to save result to sessionStorage
const saveCachedResult = (userId, result) => {
  try {
    const cacheKey = `mentor_access_${userId}`;
    sessionStorage.setItem(cacheKey, JSON.stringify(result));
  } catch (e) {
    // Ignore sessionStorage errors
  }
};

export default function MentorAccessCheck({ children }) {
  const { user } = useAuth();
  
  // Check for mentor_session in localStorage (for access code login)
  const getMentorSession = () => {
    try {
      const session = JSON.parse(localStorage.getItem('mentor_session') || '{}');
      return (session && session.ownerId && session.email) ? session : null;
    } catch (_) {
      return null;
    }
  };
  
  // Check cache synchronously before any render to avoid showing loading screen
  const getInitialState = () => {
    const mentorSession = getMentorSession();
    
    // If no Firebase user but has mentor_session, allow access (skip membership check for now)
    if (!user && mentorSession) {
      return { isChecking: false, showExpiredPopup: false, primaryOwnerEmail: '', hasChecked: true, needsApiCall: false };
    }
    
    if (!user) {
      return { isChecking: false, showExpiredPopup: false, primaryOwnerEmail: '', hasChecked: true, needsApiCall: false };
    }
    
    // Check both in-memory cache and sessionStorage synchronously
    const cacheKey = `mentor_access_${user.uid}`;
    let cached = accessCheckCache.get(cacheKey);
    
    // If not in memory cache, check sessionStorage
    if (!cached || (Date.now() - cached.timestamp) >= CACHE_DURATION) {
      cached = getCachedResult(user.uid);
      if (cached) {
        accessCheckCache.set(cacheKey, cached);
      }
    }
    
    if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
      // Valid cache exists - no API call needed, no loading screen
      if (cached.showPopup) {
        return { 
          isChecking: false, 
          showExpiredPopup: true, 
          primaryOwnerEmail: cached.primaryOwnerEmail || '',
          hasChecked: true,
          needsApiCall: false
        };
      }
      return { isChecking: false, showExpiredPopup: false, primaryOwnerEmail: '', hasChecked: true, needsApiCall: false };
    }
    
    // No valid cache - will need to make API call, but don't show loading screen immediately
    // Only show it if the API call takes time
    return { isChecking: false, showExpiredPopup: false, primaryOwnerEmail: '', hasChecked: false, needsApiCall: true };
  };
  
  const initialState = getInitialState();
  const [showExpiredPopup, setShowExpiredPopup] = useState(initialState.showExpiredPopup);
  const [isChecking, setIsChecking] = useState(initialState.isChecking);
  const [primaryOwnerEmail, setPrimaryOwnerEmail] = useState(initialState.primaryOwnerEmail);
  const [hasChecked, setHasChecked] = useState(initialState.hasChecked);
  const [needsApiCall, setNeedsApiCall] = useState(initialState.needsApiCall);

  useEffect(() => {
    const checkMentorAccess = async () => {
      const mentorSession = getMentorSession();
      
      // If no Firebase user but has mentor_session, allow access (skip membership check)
      if (!user && mentorSession) {
        setIsChecking(false);
        setHasChecked(true);
        setNeedsApiCall(false);
        return;
      }
      
      if (!user) {
        setIsChecking(false);
        setHasChecked(true);
        setNeedsApiCall(false);
        return;
      }

      // Check cache first (both in-memory and sessionStorage)
      const cacheKey = `mentor_access_${user.uid}`;
      let cached = accessCheckCache.get(cacheKey);
      
      // If not in memory cache, check sessionStorage
      if (!cached || (Date.now() - cached.timestamp) >= CACHE_DURATION) {
        cached = getCachedResult(user.uid);
        if (cached) {
          accessCheckCache.set(cacheKey, cached);
        }
      }
      
      if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        console.log('MentorAccessCheck: Using cached access check result - skipping API call');
        setIsChecking(false);
        setHasChecked(true);
        setNeedsApiCall(false);
        if (cached.showPopup) {
          setPrimaryOwnerEmail(cached.primaryOwnerEmail || '');
          setShowExpiredPopup(true);
        }
        return; // Exit early - no API call needed
      }

      // Only make API call if we don't have a valid cache
      // Don't show loading screen immediately - only if API call takes time
      if (!needsApiCall) {
        return; // Already checked or cached
      }

      try {
        // Get user profile to check if they're a mentor
        const userProfile = await getDoc(doc(db, 'users', user.uid));
        if (!userProfile.exists()) {
          setIsChecking(false);
          // Cache the result
          const cacheResult = { timestamp: Date.now(), showPopup: false };
          accessCheckCache.set(cacheKey, cacheResult);
          saveCachedResult(user.uid, cacheResult);
          return;
        }

        const userData = userProfile.data();
        const isMentor = userData.accountType === 'ngo_admin' || 
                        userData.accountType === 'art_mentor' || 
                        userData.mentorSeatsPurchased > 0 || 
                        userData.isMentor === true;

        if (!isMentor) {
          setIsChecking(false);
          // Cache the result
          const cacheResult = { timestamp: Date.now(), showPopup: false };
          accessCheckCache.set(cacheKey, cacheResult);
          saveCachedResult(user.uid, cacheResult);
          return;
        }

        // Check membership status via backend
        console.log('MentorAccessCheck: Checking membership status for user:', user.uid);
        
        // Show loading screen only if API call takes more than 500ms
        let loadingTimeout = null;
        
        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        try {
          // Set up delayed loading screen
          loadingTimeout = setTimeout(() => {
            setIsChecking(true);
          }, 500);
          
          const response = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${user.uid}`, {
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          if (loadingTimeout) clearTimeout(loadingTimeout);
          setIsChecking(false); // Hide loading screen immediately when response arrives
          
          console.log('MentorAccessCheck: Membership status response:', response.status);
          const data = await response.json();
          console.log('MentorAccessCheck: Membership status data:', data);
          
          let shouldShowPopup = false;
          let ownerEmail = '';
          
          // Only show renewal popup if membership is cancelled (not just expired)
          if (!data.allowed && data.isMentor && data.membership?.canceled) {
            // Get primary owner email for notification
            if (data.parentId) {
              try {
                const parentProfile = await getDoc(doc(db, 'users', data.parentId));
                if (parentProfile.exists()) {
                  const parentData = parentProfile.data();
                  ownerEmail = parentData.email || '';
                }
              } catch (error) {
                console.error('Error fetching primary owner email:', error);
              }
            }
            shouldShowPopup = true;
            setPrimaryOwnerEmail(ownerEmail);
            setShowExpiredPopup(true);
          }
          
          // Cache the result
          const cacheResult = {
            timestamp: Date.now(),
            showPopup: shouldShowPopup,
            primaryOwnerEmail: ownerEmail
          };
          accessCheckCache.set(cacheKey, cacheResult);
          saveCachedResult(user.uid, cacheResult);
        } catch (fetchError) {
          clearTimeout(timeoutId);
          if (loadingTimeout) clearTimeout(loadingTimeout);
          setIsChecking(false);
          console.error('MentorAccessCheck: Error fetching membership status:', fetchError);
          // Cache a "no popup" result even on error to avoid repeated failed checks
          const cacheResult = { timestamp: Date.now(), showPopup: false };
          accessCheckCache.set(cacheKey, cacheResult);
          saveCachedResult(user.uid, cacheResult);
          // Continue without showing popup if API fails
        }
      } catch (error) {
        setIsChecking(false);
        console.error('Error checking mentor access:', error);
        // Cache a "no popup" result on error
        const cacheResult = { timestamp: Date.now(), showPopup: false };
        accessCheckCache.set(cacheKey, cacheResult);
        saveCachedResult(user.uid, cacheResult);
      } finally {
        setIsChecking(false);
        setHasChecked(true);
        setNeedsApiCall(false);
      }
    };

    // Only run check if we need to make an API call
    if (needsApiCall && !hasChecked) {
      checkMentorAccess();
    }
  }, [user, needsApiCall, hasChecked]);

  const handleNotifyPrimaryOwner = async () => {
    try {
      const response = await fetch(`${backendConfig.baseUrl}/api/mentors/notify-renewal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          mentorId: user.uid,
          mentorEmail: user.email,
          primaryOwnerEmail: primaryOwnerEmail
        })
      });

      if (response.ok) {
        alert('Notification sent to your organization owner. They will receive an email to renew the cancelled plan.');
        setShowExpiredPopup(false);
      } else {
        alert('Failed to send notification. Please try again.');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Failed to send notification. Please try again.');
    }
  };

  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      
      {/* Expired Membership Popup */}
      {showExpiredPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 text-2xl">⚠️</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Organization Plan Cancelled</h3>
              <p className="text-gray-600">
                Your organization's membership was cancelled and has expired. Please notify your organization owner to renew the plan.
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={handleNotifyPrimaryOwner}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Notify Organization Owner
              </button>
              <button
                onClick={() => setShowExpiredPopup(false)}
                className="w-full border border-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
            
            <p className="text-xs text-gray-500 text-center mt-4">
              An email will be sent to your organization owner with renewal instructions.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
